import { getStoredOtpToken, storeOtpToken } from '@/hooks/use-public-schedule';
import { LoginData, RegisterData } from '@/lib/validators/auth';
import { ClientOnboardingData } from '@/lib/validators/client-onboarding';
import api from '@/services/api';
import { User } from '@/types/user';
import { USER_ROLE } from '@/types/user-role';
import { getHomePathForUser, isProviderRole } from '@/utils/user-role';
import { authPersistStorage, flushAuthPersist } from '@/lib/auth-storage';
import { isAxiosError } from 'axios';
import { router } from 'expo-router';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resetActingProvider } from './actingProviderStore';
import { showToast } from './toastStore';
import { getSupabaseClient } from '@/lib/supabase/supabase';
import {
  clearPendingAppleAuthorizationCode,
  getLastOAuthProvider,
  getPendingAppleAuthorizationCode,
} from '@/lib/oauth/google-oauth';
import { getAndClearCheckoutRedirect } from '@/components/checkout/welcome-modal';
import { shouldWebReturnToAppAfterClientRegistration } from '@/lib/auth/web-onboarding-return-to-app';
import { syncSentryUser } from '@/lib/sentry';
import { getApiErrorMessage } from '@/utils/api-error';
import { getSignupAttribution } from '@/lib/signup-attribution';

const persistAuthSession = async (user: User | null, token: string | null) => {
  await flushAuthPersist({
    user,
    token,
    isAuthenticated: !!token,
  });
};

// Helper function to navigate after successful authentication
const navigateAfterAuth = async (user: User) => {
  if (!user.isOnboarded) {
    // Non-onboarded users (e.g., Google auth) go to register to complete profile
    router.replace('/(auth)/register');
    return;
  }

  if (shouldWebReturnToAppAfterClientRegistration(user)) {
    router.replace('/return-to-app');
    return;
  }

  // Check if there's a pending checkout redirect
  const checkoutChargeId = getAndClearCheckoutRedirect();
  if (checkoutChargeId) {
    router.replace(`/checkout/${checkoutChargeId}`);
    return;
  }

  // Check if we're already on checkout route - don't redirect away
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/checkout/')) {
    return;
  }

  router.replace(getHomePathForUser(user));
};

type OAuthAuthEndpoint = '/auth/google' | '/auth/apple';

async function postSupabaseOAuthLogin(
  endpoint: OAuthAuthEndpoint,
  token: string,
  registrationData?: Partial<RegisterData>,
) {
  const payload: Record<string, unknown> = { token };

  if (registrationData) {
    payload.role = registrationData.role;
    if (registrationData.role === USER_ROLE.PROVIDER && registrationData.providerProfile) {
      payload.title = registrationData.providerProfile.title;
      payload.bio = registrationData.providerProfile.bio;
      payload.phone = registrationData.providerProfile.businessPhone;
      payload.address = registrationData.providerProfile.address;
    } else if (registrationData.role === USER_ROLE.CLIENT && registrationData.clientProfile) {
      payload.phone = registrationData.clientProfile.phone;
      payload.address = registrationData.clientProfile.address;
    }
    // Origem da primeira visita (UTM, página de entrada, host): só prestador.
    if (registrationData.role === USER_ROLE.PROVIDER) {
      Object.assign(payload, getSignupAttribution());
    }
  }

  const appleAuthorizationCode =
    endpoint === '/auth/apple' ? getPendingAppleAuthorizationCode() : null;
  if (appleAuthorizationCode) payload.appleAuthorizationCode = appleAuthorizationCode;

  const response = await api.post(endpoint, payload);
  if (appleAuthorizationCode) clearPendingAppleAuthorizationCode();
  return response;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (data: LoginData) => Promise<void>;
  loginByUid: (uid: string) => Promise<void>;
  /**
   * Convidado do agendamento público assume a conta provisória do telefone.
   * Email/senha são opcionais: o telefone provado já basta para logar depois
   * pelo WhatsApp; eles só dão um atalho a mais para outro aparelho.
   */
  claimGuestAccount: (data: {
    otpToken: string;
    email?: string;
    password?: string;
    name?: string;
  }) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  /** Dispara o código de 6 dígitos no WhatsApp. `devCode` só volta em dev. */
  requestPhoneCode: (phone: string) => Promise<{ sent: boolean; devCode?: string }>;
  /** Entra pelo WhatsApp — cria a conta se o telefone ainda não tiver uma. */
  loginWithPhone: (data: {
    phone: string;
    code: string;
    role?: USER_ROLE;
    /** Sem código: só em dev, com AUTH_PHONE_DEV_SKIP_OTP=true na API. */
    skipCode?: boolean;
  }) => Promise<{ isNew: boolean }>;
  /** Profissional convidado prova o WhatsApp do convite e assume o perfil criado pelo dono. */
  claimInvite: (data: { token: string; code: string }) => Promise<void>;
  /** Completa com email e senha a conta que nasceu só com telefone. */
  setCredentials: (data: { email: string; password: string; username?: string }) => Promise<void>;
  /** Conta com email/OAuth adiciona o WhatsApp como forma de entrar. */
  linkPhone: (data: { phone: string; code?: string }) => Promise<void>;
  /** Conta que nasceu só com telefone vincula Google/Apple. */
  linkOAuth: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  setUser: (user: User | null) => void;
  fetchProfile: () => void;
  setInitialPassword: (data: ClientOnboardingData) => Promise<void>;
  googleLogin: (token: string, registrationData?: Partial<RegisterData>) => Promise<void>;
  appleLogin: (token: string, registrationData?: Partial<RegisterData>) => Promise<void>;
  loginWithOAuthToken: (token: string, registrationData?: Partial<RegisterData>) => Promise<void>;
  loginWithHandoff: (handoffToken: string) => Promise<void>;
  completeProfile: (data: RegisterData) => Promise<void>;
  becomeProvider: (data: {
    title: string;
    businessPhone: string;
    bio?: string;
    address?: string;
  }) => Promise<void>;
  addClientProfile: (data: { phone: string; address?: string }) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (data) => {
        try {
          const response = await api.post('/auth', data);

          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Login efetuado com sucesso!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          // Navigate after successful login
          await navigateAfterAuth(user);
        } catch (error) {
          console.error('Login failed:', error);
          showToast('Falha ao tentar logar!', 'error');
          const apiErrorMessage =
            getApiErrorMessage(error, 'Falha no login. Verifique suas credenciais.');

          throw new Error(apiErrorMessage);
        }
      },

      loginByUid: async (uid) => {
        try {
          const response = await api.post('/auth/dev/login-by-uid', { uid });

          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Login por UID efetuado com sucesso!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          await navigateAfterAuth(user);
        } catch (error) {
          console.error('Login by UID failed:', error);
          showToast(
            getApiErrorMessage(error, 'Usuário não encontrado. Verifique o UID.'),
            'error',
          );
          throw new Error(getApiErrorMessage(error, 'Falha no login por UID.'));
        }
      },

      claimGuestAccount: async (data) => {
        try {
          const response = await api.post('/public/schedule/claim-account', data);

          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Conta criada! Seus agendamentos estão aqui.', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          await navigateAfterAuth(user);
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível criar sua conta. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      register: async (data) => {
        try {
          let response;

          switch (data.role) {
            case USER_ROLE.PROVIDER:
              // Título/telefone/bio não são pedidos no cadastro: o backend usa
              // o nome como título e o resto vem do onboarding e do perfil.
              response = await api.post('/auth/register/provider', {
                email: data.email,
                username: data.username,
                password: data.password,
                role: data.role,
                title: data.providerProfile?.title,
                bio: data.providerProfile?.bio,
                address: data.providerProfile?.address,
                businessPhone: data.providerProfile?.businessPhone,
                utmSource: (data as any).utmSource,
                utmMedium: (data as any).utmMedium,
                utmCampaign: (data as any).utmCampaign,
                utmContent: (data as any).utmContent,
                utmTerm: (data as any).utmTerm,
                fbclid: (data as any).fbclid,
                fbc: (data as any).fbc,
                fbp: (data as any).fbp,
                metaCompleteRegistrationEventId: (data as any).metaCompleteRegistrationEventId,
              });
              response.data.user.providerProfile = response.data.provider;
              break;

            case USER_ROLE.CLIENT:
              if (!data.clientProfile) {
                throw new Error('Não foi possível criar seu perfil. Tente novamente.');
              }

              // Convidado que já agendou neste device: o token do telefone
              // verificado (OTP) faz a conta nova assumir o cliente provisório,
              // com os agendamentos guest junto. O backend só aceita se o
              // telefone do token bater com o do cadastro.
              const guestOtpToken = await getStoredOtpToken(data.clientProfile.phone);

              response = await api.post('/auth/register/client', {
                email: data.email,
                username: data.username,
                password: data.password,
                role: data.role,
                address: data.clientProfile.address,
                phone: data.clientProfile.phone,
                otpToken: guestOtpToken ?? undefined,
              });
              response.data.user.clientProfile = response.data.client;
              break;
            default:
              throw new Error('Tipo de conta inválido.');
          }

          if (response?.data?.accessToken) {
            const { accessToken, user } = response.data;
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            showToast('Cadastro realizado com sucesso!', 'success');
            set({ user, token: accessToken, isAuthenticated: true });
            await persistAuthSession(user, accessToken);

            // Navigate after successful registration
            await navigateAfterAuth(user);
          } else {
            showToast('Cadastro realizado com sucesso! Faça login para continuar.', 'success');
            router.replace('/(auth)/login');
          }
        } catch (error) {
          console.error('Registration failed:', error);

          throw new Error(getApiErrorMessage(error, 'Falha no cadastro. Tente novamente.'));
        }
      },

      requestPhoneCode: async (phone) => {
        try {
          const response = await api.post<{ sent: boolean; devCode?: string }>(
            '/auth/phone/request-code',
            { phone },
          );
          return response.data;
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível enviar o código. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      loginWithPhone: async ({ phone, code, role, skipCode }) => {
        try {
          const endpoint = skipCode ? '/auth/phone/dev/verify' : '/auth/phone/verify';
          const response = await api.post(endpoint, {
            phone,
            ...(skipCode ? {} : { code }),
            role,
            // Origem da primeira visita: o backend só grava se a conta nascer
            // agora como prestador.
            ...(role !== USER_ROLE.CLIENT && getSignupAttribution()),
          });

          const { user, accessToken, otpToken, isNew } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          // Mesmo telefone, mesma prova: guardar o token deixa os agendamentos
          // feitos como convidado continuarem alcançáveis por este device.
          if (otpToken) {
            await storeOtpToken(phone, otpToken);
          }

          showToast(
            isNew ? 'Conta criada! Vamos configurar.' : 'Login efetuado com sucesso!',
            'success',
          );

          // Prestador recém-criado vai direto para a conversa de configuração;
          // o resto segue o roteamento normal de pós-login.
          if (isNew && isProviderRole(user.role)) {
            router.replace('/onboarding');
          } else {
            await navigateAfterAuth(user);
          }

          return { isNew: !!isNew };
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível entrar com este telefone. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      claimInvite: async ({ token, code }) => {
        try {
          const response = await api.post(`/public/invite/${token}/claim`, { code });
          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          // Sessão nova, de outra pessoa: foco antigo neste aparelho não vale.
          resetActingProvider();
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          showToast('Você entrou na equipe!', 'success');
          router.replace('/(tabs)/(provider)/schedule' as never);
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível confirmar o convite. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      setCredentials: async ({ email, password, username }) => {
        try {
          const response = await api.post('/auth/credentials', {
            email,
            password,
            username,
          });

          const { user, accessToken } = response.data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          showToast('Email e senha salvos!', 'success');
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível salvar email e senha. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      linkPhone: async ({ phone, code }) => {
        try {
          const response = await api.post('/auth/phone/link', { phone, code });
          const { user, accessToken } = response.data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          showToast('WhatsApp vinculado!', 'success');
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível vincular o WhatsApp. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      linkOAuth: async (token) => {
        try {
          const appleAuthorizationCode =
            getLastOAuthProvider() === 'apple' ? getPendingAppleAuthorizationCode() : null;
          const response = await api.post('/auth/oauth/link', {
            token,
            ...(appleAuthorizationCode ? { appleAuthorizationCode } : {}),
          });
          if (appleAuthorizationCode) clearPendingAppleAuthorizationCode();
          const { user, accessToken } = response.data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          showToast('Conta vinculada!', 'success');
        } catch (error) {
          const apiErrorMessage =
            getApiErrorMessage(error, 'Não foi possível vincular a conta. Tente novamente.');
          throw new Error(apiErrorMessage);
        }
      },

      logout: async () => {
        delete api.defaults.headers.common['Authorization'];
        resetActingProvider();
        await getSupabaseClient()?.auth.signOut();
        set({ user: null, token: null, isAuthenticated: false });
        await persistAuthSession(null, null);
        // Navegação será tratada pelo InitialLayout
      },

      deleteAccount: async () => {
        try {
          await api.delete('/user/me');
          showToast('Conta excluída com sucesso.', 'success');
        } catch (error) {
          const message =
            getApiErrorMessage(error, 'Não foi possível excluir a conta. Tente novamente.');
          showToast(message, 'error');
          throw new Error(message);
        } finally {
          await get().logout();
        }
      },

      fetchProfile: async () => {
        console.log('fetching profile');
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const response = await api.get<User>('/user/me');

          set({
            user: response.data,
            isAuthenticated: true,
          });
          const pp = response.data.providerProfile;
          console.log('[fetchProfile] Perfil carregado:', {
            providerStatus: pp?.status,
            subscriptionId: pp?.subscriptionId,
            subscriptionStatus: pp?.subscriptionStatus,
            hasSubscription: !!(pp?.subscriptionId && pp?.subscriptionStatus !== 'canceled'),
          });
        } catch (error) {
          const status = isAxiosError(error) ? error.response?.status : undefined;
          if (status === 401 || status === 403) {
            console.error('fetchProfile: sessão inválida, fazendo logout', error);
            get().logout();
          } else {
            console.error('fetchProfile: falha ao buscar perfil (mantendo sessão)', error);
          }
        }
      },

      setUser: (user) => {
        set({ user });
      },

      setInitialPassword: async (data) => {
        try {
          const response = await api.post('/auth/set-initial-password', {
            token: data.token,
            username: data.username,
            newPassword: data.newPassword,
            phone: data.phone,
            address: data.address,
          });

          const { user, accessToken } = response.data;
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Cadastro concluído com sucesso!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);

          // Navigate after successful setup
          await navigateAfterAuth(user);
        } catch (error) {
          console.error('Set initial password failed:', error);

          throw new Error(
            getApiErrorMessage(error, 'Falha ao configurar senha inicial. Tente novamente.'),
          );
        }
      },

      loginWithHandoff: async (handoffToken) => {
        try {
          const response = await api.post('/auth/app-handoff/claim', { handoffToken });
          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Bem-vindo à Controlapag!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          await navigateAfterAuth(user);
        } catch (error) {
          console.error('App handoff failed:', error);
          showToast('Não foi possível abrir sua sessão no app. Faça login.', 'error');
          router.replace('/(auth)/login');
          throw error;
        }
      },

      googleLogin: async (token, registrationData) => {
        try {
          const response = await postSupabaseOAuthLogin('/auth/google', token, registrationData);
          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Login com Google efetuado com sucesso!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          await navigateAfterAuth(user);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 404) {
            router.replace('/(auth)/register');
            return;
          }

          console.error('Google login failed:', error);
          showToast('Falha ao tentar logar com Google!', 'error');
          const apiErrorMessage =
            getApiErrorMessage(error, 'Falha no login com Google.');

          throw new Error(apiErrorMessage);
        }
      },

      appleLogin: async (token, registrationData) => {
        try {
          const response = await postSupabaseOAuthLogin('/auth/apple', token, registrationData);
          const { user, accessToken } = response.data;

          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          showToast('Login com Apple efetuado com sucesso!', 'success');
          set({ user, token: accessToken, isAuthenticated: true });
          await persistAuthSession(user, accessToken);
          await navigateAfterAuth(user);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 404) {
            router.replace('/(auth)/register');
            return;
          }

          console.error('Apple login failed:', error);
          showToast('Falha ao tentar logar com Apple!', 'error');
          const apiErrorMessage =
            getApiErrorMessage(error, 'Falha no login com Apple.');

          throw new Error(apiErrorMessage);
        }
      },

      loginWithOAuthToken: async (token, registrationData) => {
        const provider = getLastOAuthProvider();
        if (provider === 'apple') {
          await get().appleLogin(token, registrationData);
          return;
        }
        await get().googleLogin(token, registrationData);
      },

      completeProfile: async (data) => {
        try {
          // If we don't have a backend user but might have a Supabase session (Google atomic registration)
          if (!get().user) {
            const {
              data: { session },
            } = (await getSupabaseClient()?.auth.getSession()) ?? { data: { session: null } };
            if (session?.access_token) {
              await get().loginWithOAuthToken(session.access_token, data);
              return;
            }
          }

          const payload = {
            role: data.role,
            phone:
              data.role === USER_ROLE.PROVIDER
                ? data.providerProfile?.businessPhone
                : data.clientProfile?.phone,
            address:
              data.role === USER_ROLE.PROVIDER
                ? data.providerProfile?.address
                : data.clientProfile?.address,
            title: data.providerProfile?.title,
            bio: data.providerProfile?.bio,
            ...(data.role === USER_ROLE.PROVIDER && getSignupAttribution()),
          };

          const response = await api.post('/auth/complete-profile', payload);
          const { user } = response.data;

          if (user) {
            set({ user, isAuthenticated: true });

            // Navigate after completing profile
            await navigateAfterAuth(user);
          }
          showToast('Perfil configurado com sucesso!', 'success');
        } catch (error) {
          console.error('Complete profile failed:', error);
          showToast('Falha ao configurar perfil!', 'error');
          const apiErrorMessage =
            getApiErrorMessage(error, 'Falha ao completar perfil.');
          throw new Error(apiErrorMessage);
        }
      },

      becomeProvider: async (data) => {
        try {
          const response = await api.post('/auth/become-provider', {
            ...data,
            ...getSignupAttribution(),
          });
          const { user } = response.data;
          if (user) {
            set({ user, isAuthenticated: true });
            showToast(
              'Agora você é prestador! Seus contratos como cliente foram mantidos.',
              'success',
            );
            await navigateAfterAuth(user);
          }
        } catch (error) {
          console.error('Become provider failed:', error);
          showToast(getApiErrorMessage(error, 'Falha ao virar prestador.'), 'error');
          throw error;
        }
      },

      addClientProfile: async (data) => {
        try {
          const response = await api.post('/auth/add-client-profile', data);
          const { user } = response.data;
          if (user) {
            set({ user, isAuthenticated: true });
            showToast(
              'Perfil de cliente adicionado! Agora você pode receber contratos de outros prestadores.',
              'success',
            );
          }
        } catch (error) {
          console.error('Add client profile failed:', error);
          showToast(
            getApiErrorMessage(error, 'Falha ao adicionar perfil de cliente.'),
            'error',
          );
          throw error;
        }
      },
    }),
    {
      name: 'user-storage',
      storage: authPersistStorage,
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
        }
        syncSentryUser(state?.user ?? null);
      },
    },
  ),
);

// Sentry precisa saber quem é o usuário em qualquer login/logout — os dois só
// diferem no `user` (id muda ou vira null), então um listener aqui cobre
// todos os fluxos (email, telefone, OAuth, convite) sem repetir em cada um.
useAuthStore.subscribe((state, prevState) => {
  if (state.user?.id === prevState.user?.id) return;
  syncSentryUser(state.user);
});
